import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { MigrationSubmitRequest } from '@devrobot/shared';
import { ArrowRightLeft, Info, TriangleAlert } from 'lucide-react';
import { ApiError, newIdempotencyKey } from '../../../api/client';
import { submitMigration, useApiMutation } from '../../../api/queries';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { SkeletonLines } from '../../../components/Skeleton';
import { Haptic } from '../../../kit/haptics';
import { haptic } from '../../../kit/native';
import { KindBadge, PlanSummary, SourceToDestination } from '../PlanSummary';
import { toRequest, type StepProps } from './model';

export function ReviewStep({
  state,
  preview,
  sourceBackupIds,
  destBackupIds,
  onBackToSafety,
}: StepProps & { sourceBackupIds: string[]; destBackupIds: string[]; onBackToSafety: () => void }) {
  const navigate = useNavigate();
  const inputId = useId();
  const plan = preview.plan;
  const [typed, setTyped] = useState('');
  const [planChanged, setPlanChanged] = useState(false);
  const submit = useApiMutation((v: { body: MigrationSubmitRequest; key: string }) => submitMigration(v.body, v.key), { invalidate: [['migrations'], ['jobs']] });

  // Always review a freshly computed plan.
  const { refetch } = preview;
  useEffect(() => {
    refetch();
  }, [refetch]);

  // One submission intent per plan: a new plan gets a new idempotency key and must be re-confirmed.
  const idem = useRef({ hash: '', key: newIdempotencyKey() });
  if (plan && idem.current.hash !== plan.planHash) idem.current = { hash: plan.planHash, key: newIdempotencyKey() };

  if (!plan) {
    return preview.error ? <ErrorAlert error={preview.error} title="The plan could not be computed" onRetry={preview.refetch} /> : <SkeletonLines lines={10} />;
  }

  const matches = typed === plan.confirmPhrase;
  const ready = preview.upToDate && !preview.fetching;
  const blocked = !plan.canMigrate || plan.blockers.length > 0;
  const disabled = !matches || !ready || blocked || submit.isPending;
  const err = submit.error;
  const prerequisite = err instanceof ApiError && err.status === 412;

  const onSubmit = () => {
    if (disabled) return;
    const body: MigrationSubmitRequest = { ...toRequest(state), planHash: plan.planHash, confirm: typed, sourceBackupIds, destBackupIds };
    setPlanChanged(false);
    submit.mutate(
      { body, key: idem.current.key },
      {
        onSuccess: (r) => navigate(`/migrations/${r.migration.id}`),
        onError: (e) => {
          haptic('error');
          if (e instanceof ApiError && e.status === 409 && e.code === 'plan_changed') {
            setPlanChanged(true);
            setTyped('');
            submit.reset();
            preview.refetch();
          }
        },
      },
    );
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <KindBadge kind={plan.kind} />
        <span className="font-medium">{plan.project.name}</span>
        <SourceToDestination source={plan.source} destination={plan.destination} />
        {preview.fetching ? <span className="loading loading-spinner loading-xs" aria-label="Updating the plan" /> : null}
      </div>

      {planChanged ? (
        <div role="status" className="alert alert-warning alert-soft text-sm">
          <Info className="size-4 shrink-0" aria-hidden="true" />
          <span>Something changed since the plan was computed (for example a deployment or configuration change). The plan below is fresh: review it and confirm again.</span>
        </div>
      ) : null}
      {preview.error ? <ErrorAlert error={preview.error} title="The plan could not be refreshed" onRetry={preview.refetch} /> : null}

      <div className={`transition-opacity ${ready ? '' : 'opacity-60'}`}>
        <PlanSummary
          plan={plan}
          selection={{ databases: state.include.databases, volumes: state.include.volumes, variables: state.include.config.variables, files: state.include.config.files }}
        />
      </div>

      {state.overwriteDestination ? (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span>Data at the destination will be replaced.</span>
        </div>
      ) : null}

      <fieldset className="fieldset max-w-xl">
        <label htmlFor={inputId} className="fieldset-legend">
          <span>
            Type <code className="rounded-md bg-base-200 px-1.5 py-0.5 font-mono text-[0.85em]">{plan.confirmPhrase}</code> to confirm
          </span>
        </label>
        <input
          id={inputId}
          className={`input w-full font-mono ${typed && !matches ? 'input-error' : ''}`}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          value={typed}
          disabled={blocked}
          onChange={(e) => setTyped(e.target.value)}
          aria-describedby={`${inputId}-hint`}
        />
        <p id={`${inputId}-hint`} className="label whitespace-normal">
          {blocked ? 'Resolve the blockers above first.' : typed && !matches ? 'The text does not match yet.' : 'The migration starts right away and runs as a job; you can follow it on the next page.'}
        </p>
      </fieldset>

      {err ? (
        <div className="space-y-2">
          <ErrorAlert error={err} title={prerequisite ? 'A safety prerequisite is not met' : 'The migration was not started'} />
          {prerequisite ? (
            <button type="button" className="btn btn-sm" onClick={onBackToSafety}>
              Back to “Back up first”
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex justify-end">
        <Haptic disabled={disabled}>
          <button type="submit" className="btn btn-error" disabled={disabled}>
            {submit.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <ArrowRightLeft className="size-4" aria-hidden="true" />}
            Start migration
          </button>
        </Haptic>
      </div>
    </form>
  );
}
